import { useCallback, useRef } from "react"

export interface KeyboardHandlerOptions {
	debounceMs?: number
	preventDefault?: boolean
}

export const useKeyboardHandler = (
	onActivate: () => void,
	options: KeyboardHandlerOptions = { debounceMs: 100, preventDefault: true }
) => {
	const { debounceMs = 100, preventDefault = true } = options
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				if (preventDefault) {
					event.preventDefault()
				}

				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current)
				}

				timeoutRef.current = setTimeout(() => {
					onActivate()
				}, debounceMs)
			}
		},
		[onActivate, debounceMs, preventDefault]
	)

	return handleKeyDown
}

export const createKeyboardHandler = (
	onActivate: () => void,
	options: KeyboardHandlerOptions = { debounceMs: 100, preventDefault: true }
) => {
	const { debounceMs = 100, preventDefault = true } = options
	let timeoutId: NodeJS.Timeout | null = null

	return (event: React.KeyboardEvent) => {
		if (event.key === "Enter" || event.key === " ") {
			if (preventDefault) {
				event.preventDefault()
			}

			if (timeoutId) {
				clearTimeout(timeoutId)
			}

			timeoutId = setTimeout(() => {
				onActivate()
			}, debounceMs)
		}
	}
}

export const useCheckboxKeyboardHandler = (
	onToggle: () => void,
	options: KeyboardHandlerOptions = {}
) => {
	return useKeyboardHandler(onToggle, { debounceMs: 100, ...options })
}

export const useButtonKeyboardHandler = (
	onAction: () => void,
	isDisabled: boolean,
	actionName: string,
	options: KeyboardHandlerOptions = {}
) => {
	const handleKeyDown = useKeyboardHandler(onAction, {
		debounceMs: 100,
		...options
	})

	return {
		onKeyDown: handleKeyDown,
		ariaLabel: `${actionName} (${isDisabled ? "disabled" : "enabled"})`,
		ariaDisabled: isDisabled,
		cursor: isDisabled ? "not-allowed" : "pointer"
	}
}
